/**
 * DropZone — drop target for .gi files. Parses the file client-side
 * and hands the resulting `GiBundle` up; parse failures surface via
 * the `onError` callback.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import type { GiBundle } from '@/api/types';
import { cn } from '@/utils/cn';
import { BundleParseError, verify } from '@/api/endpoints/verify';

export function DropZone({
  onBundle,
  onError,
  disabled,
}: {
  onBundle: (b: GiBundle) => void;
  onError: (msg: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('verify');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);

  async function handleFile(file: File) {
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const bundle = verify.parseBytes(bytes);
      onBundle(bundle);
    } catch (e) {
      if (e instanceof BundleParseError) onError(e.message);
      else if (e instanceof SyntaxError) onError(t('errors.not_json'));
      else onError((e as Error).message);
    }
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={async (e) => {
        e.preventDefault();
        setDragActive(false);
        if (disabled) return;
        const f = e.dataTransfer.files?.[0];
        if (f) await handleFile(f);
      }}
      className={cn(
        'flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-colors',
        dragActive
          ? 'border-spark bg-spark-soft/40'
          : 'border-border bg-surface',
        disabled ? 'opacity-60' : '',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".gi,application/json,application/octet-stream"
        className="sr-only"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) await handleFile(f);
        }}
      />
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-spark-soft text-spark">
        <DropGlyph />
      </span>
      <p className="text-sm font-medium text-ink">{t('drop.title')}</p>
      <p className="text-xs text-ink-muted">{t('drop.sub')}</p>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
      >
        {t('drop.browse')}
      </Button>
    </div>
  );
}

function DropGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20V8M6 14l6-6 6 6M4 20h16" transform="translate(0 -2)" />
    </svg>
  );
}
